import React, { useCallback, useContext, useMemo, useState } from 'react';
import PageLayout from '../pagelayout';
import {
  Table,
  TableCellTd,
  TableCellTh,
  TableHeader,
  TableMain,
  TableRow,
} from '../components/table/alpTable';
import translations from './practiceplaces.i18n';
import { useIntl } from 'react-intl';
import { ToolbarLink } from '../pagelayout/toolbarLink';
import Icon, { IconType } from '../components/icon';
import * as css from './practiceplaces.module.scss';

import { StudiestedApiContext } from '../api/StudiestedApiContext';
import { useLoader } from '../api/useLoader';
import {
  BrukerinfoDto,
  BrukerrollerDto,
  PraksisstedDto,
  PraksisstedDtoBrukerStatusDtoEnum,
} from '../api/generated/studiested-generated-api';
import Spinner from '../components/spinner/spinner';
import { NavLink, Outlet, Route, Routes, useNavigate } from 'react-router-dom';
import { UhaUserContext } from '../user/UhaUserStore';
import {
  OnStedHoverCard,
  StedType,
} from '../components/hierarchy/onhoverhierarchyinfo';
import AddPracticePlace from './addpracticeplace';
import { InvitePsUserPageUHA } from '../invite/invitePsUserPage';
import PracticePlaceProfilePageUha from './practiceplaceprofile/practiceplaceprofilepage.uha';
import PraksisstedbrukerprofilPage from '../userprofile/praksisstedbrukerprofilpage.uha';
import PageNotFoundPage, {
  PageNotFoundWithoutLayout,
} from '../pages/pagenotfound';
import { Tab, Tabs } from '../components/tabs/tabs';
import ContactPersonsPage from './contactpersonspage';
import {
  AgreementsDraftsPage,
  AgreementsPage,
} from './practiceplaceagreement/agreementtabs';
import { EmptyState } from '../components/emptyState/emptyState';
import { PracticePlaceAgreementProviderUH } from './practiceplaceagreement';
import Button, { ArrowToggleButton } from '../components/button';
import { FlexSpacer } from '../components/spacer/spacer';
import classNames from 'classnames';
import { devEnvFlag } from '../util/featureFlags';

const PracticePlacesPage = () => {
  const { uhaUser: bruker } = useContext(UhaUserContext);

  const { formatMessage } = useIntl();
  const labels = {
    title: formatMessage({ id: translations.title.id }),
    subtitle: formatMessage({ id: translations.subtitle.id }),
    registerplace: formatMessage({ id: translations.registerplace.id }),
    registeragreement: formatMessage({ id: translations.registeragreement.id }),
    tabAllePraksissteder: formatMessage({
      id: translations.tabAllePraksissteder.id,
    }),
    tabKontaktpersoner: formatMessage({
      id: translations.tabKontaktpersoner.id,
    }),
    tabAvtaler: formatMessage({ id: translations.tabAvtaler.id }),
    tabAvtaleutkast: formatMessage({ id: translations.tabAvtaleutkast.id }),
  };

  const registerButton: React.ReactNode = ToolbarLink({
    key: 'btn-registerplace',
    title: labels.registerplace,
    exact: true,
    path: 'addpracticeplace',
    icon: <Icon type={IconType.AddCircleRounded} />,
  });

  const registerAgreementButton: React.ReactNode = ToolbarLink({
    key: 'btn-registeragreement',
    title: labels.registeragreement,
    exact: true,
    path: 'practiceplaceagreement',
    icon: <Icon type={IconType.AddCircleRounded} />,
  });

  const canRegister =
    (bruker &&
      (bruker.hoeyesteRettighet === BrukerrollerDto.Registrererettighet ||
        bruker.hoeyesteRettighet === BrukerrollerDto.Administrator)) ||
    false;

  return (
    <Routes>
      {canRegister && (
        <Route path="/addpracticeplace" element={<AddPracticePlace />} />
      )}
      {canRegister && (
        <Route
          path="/practiceplaceagreement"
          element={<PracticePlaceAgreementProviderUH />}
        />
      )}
      <Route
        path="/practiceplaceagreement/:avtaleId"
        element={<PracticePlaceAgreementProviderUH />}
      />
      <Route path="/invitepsa/:placeId?" element={<InvitePsUserPageUHA />} />
      <Route path="/profile/:id" element={<PracticePlaceProfilePageUha />} />
      <Route
        path="/praksisstedbrukerprofil/:id"
        element={<PraksisstedbrukerprofilPage />}
      />
      <Route
        path="/"
        element={
          <PageLayout
            isIndented
            title={labels.title}
            subtitle={labels.subtitle}
            actionItems={
              canRegister
                ? [registerButton, registerAgreementButton]
                : undefined
            }
          >
            <Tabs>
              <Tab to="" end>
                {labels.tabAllePraksissteder}
              </Tab>
              {devEnvFlag && (
                <Tab to="contact">{labels.tabKontaktpersoner}</Tab>
              )}
              <Tab to="agreements">{labels.tabAvtaler}</Tab>
              <Tab to="agreementdrafts">{labels.tabAvtaleutkast}</Tab>
            </Tabs>
            <Outlet />
          </PageLayout>
        }
      >
        {devEnvFlag && (
          <Route path="contact" element={<ContactPersonsPage />} />
        )}
        <Route path="agreements" element={<AgreementsPage />} />
        <Route path="agreementdrafts" element={<AgreementsDraftsPage />} />
        <Route path="/" element={<PracticePlacesWrapper />} />
        <Route path="*" element={<PageNotFoundWithoutLayout />} />
      </Route>
      <Route path="*" element={<PageNotFoundPage />} />
    </Routes>
  );
};

const getExpandedStateFromSessionStorage = (
  userId: string,
): Expanded | undefined =>
  JSON.parse(
    sessionStorage.getItem(`expandedPraksissteder-practiceplaces-${userId}`) ||
      'null',
  ) || undefined;

const putExpandedStateInSessionStorage = (userId: string, expanded: Expanded) =>
  sessionStorage.setItem(
    `expandedPraksissteder-practiceplaces-${userId}`,
    JSON.stringify(expanded),
  );

type Expanded = { [praksisstedId: string]: boolean };

// Exported for tests
export const PracticePlacesWrapper: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const { praksisstedApi } = useContext(StudiestedApiContext);
  const { uhaUser: bruker } = useContext(UhaUserContext);

  const { data, loading } = useLoader(
    () => praksisstedApi.praksisstederGet({}),
    [],
    true,
    true,
  );

  if (loading) {
    return <Spinner />;
  }

  if (bruker && data) {
    return <PracticePlaces bruker={bruker} praksissteder={data} />;
  }

  return <></>;
};

interface PracticePlacesProps {
  bruker: BrukerinfoDto;
  praksissteder: PraksisstedDto[];
}

const PracticePlaces: React.FC<
  React.PropsWithChildren<PracticePlacesProps>
> = ({ bruker, praksissteder }) => {
  const [activeCol, setActiveCol] = useState<number>(-1);
  const [search, setSearch] = useState<string>('');
  const [expanded, setExpanded] = useState<Expanded>(
    getExpandedStateFromSessionStorage(bruker.id) || {},
  );

  const stedIdsToClosestParentId = useMemo(
    () => mapStedIdsToClosestParentId(praksissteder),
    [praksissteder],
  );

  const haveChildren = useMemo(
    () => [...new Set(Object.values(stedIdsToClosestParentId))],
    [stedIdsToClosestParentId],
  );

  const setExpandedAndUpdateSessionStorage = (expand: Expanded) => {
    putExpandedStateInSessionStorage(bruker.id, expand);
    setExpanded(expand);
  };

  const toggleExpanded = (praksisstedId: string) => {
    const shouldExpand = !expanded[praksisstedId];

    if (shouldExpand) {
      setExpandedAndUpdateSessionStorage({
        ...expanded,
        [praksisstedId]: true,
      });
    } else {
      setExpandedAndUpdateSessionStorage({
        ...expanded,
        [praksisstedId]: false,
        ...collapseAllUndernoder(praksisstedId, filteredPracticeplaces),
      });
    }
  };

  const selfOrParentIsExpanded = useCallback(
    (sted: PraksisstedDto): boolean => {
      const { id } = sted.praksissted;

      return (
        expanded[id] ||
        sted.level === 0 ||
        expanded[stedIdsToClosestParentId[id] || '']
      );
    },
    [stedIdsToClosestParentId, expanded],
  );

  const { formatMessage } = useIntl();
  const labels = {
    praksissted: formatMessage({ id: translations.praksissted.id }),
    status: formatMessage({ id: translations.status.id }),
    handling: formatMessage({ id: translations.handling.id }),
  };

  const tableHeaders = [
    { title: labels.praksissted },
    { title: labels.status },
    { title: labels.handling },
  ];

  const navigate = useNavigate();
  const inviteUser = (id: string): void => {
    navigate(`invitepsa/${id}`);
  };

  const getBrukerStatusLabel = (
    brukerStatus: PraksisstedDtoBrukerStatusDtoEnum,
  ): string => {
    const statusLabels = {
      harTilgang: formatMessage({ id: translations.harTilgang.id }),
      erInvitert: formatMessage({ id: translations.erInvitert.id }),
      ikkeInvitert: formatMessage({ id: translations.ikkeInvitert.id }),
    };

    switch (brukerStatus) {
      case PraksisstedDtoBrukerStatusDtoEnum.HarBruker:
        return statusLabels.harTilgang;
      case PraksisstedDtoBrukerStatusDtoEnum.Invitert:
        return statusLabels.erInvitert;
      case PraksisstedDtoBrukerStatusDtoEnum.IkkeInvitert:
        return statusLabels.ikkeInvitert;
    }
  };

  const filteredPracticeplaces = useMemo(
    () =>
      (praksissteder || []).filter(
        (sted) =>
          (search.length === 0 && selfOrParentIsExpanded(sted)) ||
          (search.length > 0 &&
            sted.praksissted.navn.toLowerCase().indexOf(search) > -1),
      ),
    [praksissteder, search, selfOrParentIsExpanded],
  );

  return (
    <Table>
      <TableHeader>
        <TableRow top>
          {tableHeaders.map(({ title }, index) => (
            <TableCellTh
              key={title}
              colIndex={index}
              activeCol={index === activeCol}
              setActiveCol={setActiveCol}
              search={(col, value) => setSearch(value.toLowerCase())}
              canSearch={index === 0} // Only column praksissteder can search
            >
              {title}
            </TableCellTh>
          ))}
        </TableRow>
      </TableHeader>
      <TableMain>
        {filteredPracticeplaces.map((sted, idx) => (
          <StedRad
            hasChildren={haveChildren.includes(sted.praksissted.id)}
            toggleExpand={toggleExpanded}
            isExpanded={expanded[sted.praksissted.id]}
            key={`${sted.praksissted.id}-${idx}`}
            praksisstedDto={sted}
            handleInvite={inviteUser}
            headers={tableHeaders}
            getBrukerStatusLabel={getBrukerStatusLabel}
          />
        ))}
      </TableMain>
    </Table>
  );
};

type StedByParent = { [stedId: string]: string };

const useInviteUserButtonLabel = (
  brukerStatus: PraksisstedDtoBrukerStatusDtoEnum,
): string => {
  const { formatMessage } = useIntl();
  const labels = {
    inviterNyBruker: formatMessage({ id: translations.inviterNy.id }),
    inviterTilAlp: formatMessage({ id: translations.inviterTilAlp.id }),
  };

  switch (brukerStatus) {
    case PraksisstedDtoBrukerStatusDtoEnum.HarBruker:
    case PraksisstedDtoBrukerStatusDtoEnum.Invitert:
      return labels.inviterNyBruker;
    case PraksisstedDtoBrukerStatusDtoEnum.IkkeInvitert:
      return labels.inviterTilAlp;
  }
};

const collapseAllUndernoder = (
  praksisstedId: string,
  praksissteder: PraksisstedDto[],
): Expanded => {
  const praksissted = praksissteder.find(
    (sted) => sted.praksissted.id === praksisstedId,
  );
  const praksisstedIdx = praksissteder.findIndex(
    (sted) => sted.praksissted.id === praksisstedId,
  );

  if (!praksissted || praksisstedIdx === -1) return {};

  const sliceFrom = praksissteder.slice(praksisstedIdx + 1);
  const sliceTo =
    sliceFrom.findIndex((sted) => sted.level === praksissted.level) ||
    sliceFrom.length;
  const slice = sliceFrom.slice(0, sliceTo);

  return slice.reduce(
    (acc: Expanded, sted: PraksisstedDto) => ({
      ...acc,
      [sted.praksissted.id]: false,
    }),
    {},
  );
};

const mapStedIdsToClosestParentId = (
  steder: PraksisstedDto[],
): StedByParent => {
  if (steder.length === 0) return {};

  let prevParents: PraksisstedDto[] = [];

  return steder.reduce((acc: StedByParent, sted: PraksisstedDto, idx) => {
    const { id } = sted.praksissted;

    if (sted.level === 0) {
      prevParents = [sted];
      return acc;
    }

    const prevElement = steder[idx - 1];

    if (prevElement.level !== 0) {
      if (sted.level > prevElement.level) {
        prevParents = [...prevParents, prevElement];
      } else if (sted.level < prevElement.level) {
        prevParents = prevParents.slice(0, sted.level - prevElement.level);
      }
    }

    return {
      ...acc,
      [id]: prevParents[prevParents.length - 1].praksissted.id,
    };
  }, {});
};

interface StedRadProps {
  praksisstedDto: PraksisstedDto;
  headers: { title: string }[];
  handleInvite: (id: string) => void;
  getBrukerStatusLabel: (
    brukerStatus: PraksisstedDtoBrukerStatusDtoEnum,
  ) => string;
  hasChildren: boolean;
  toggleExpand: (stedId: string) => void;
  isExpanded: boolean;
}

export const StedRad: React.FC<React.PropsWithChildren<StedRadProps>> = ({
  praksisstedDto,
  handleInvite,
  headers,
  getBrukerStatusLabel,
  hasChildren,
  toggleExpand,
  isExpanded,
}) => {
  const { praksissted, level, brukerStatus, canInvite } = praksisstedDto;

  const inviteUserButtonLabel = useInviteUserButtonLabel(brukerStatus);

  let tabHeader = 0;
  return (
    <TableRow id={praksissted.id}>
      <TableCellTd header={headers[tabHeader++].title}>
        <div className={css.practiceplace}>
          <FlexSpacer width={level}>
            {hasChildren ? (
              <ArrowToggleButton
                iconType={
                  isExpanded
                    ? IconType.CloseRowTriangle
                    : IconType.OpenRowTriangle
                }
                onClick={() => toggleExpand(praksissted.id)}
                className={classNames(css.toggleButton, css.icon)}
              />
            ) : (
              <div className={classNames(css.endNode, css.icon)}>
                <Icon type={IconType.EndNode} />
              </div>
            )}
            <div
              className={
                level === 0
                  ? css.practiceplaceNameStrong
                  : css.practiceplaceName
              }
            >
              <NavLink
                to={`/practiceplaces/profile/${praksissted.id}`}
                title={praksissted.navn}
              >
                {praksissted.navn}
              </NavLink>
            </div>
            <OnStedHoverCard
              id={praksissted.id}
              navn={praksissted.navn}
              sted={StedType.PRAKSISSTED}
            />
          </FlexSpacer>
        </div>
      </TableCellTd>
      <TableCellTd header={headers[tabHeader++].title}>
        {getBrukerStatusLabel(brukerStatus)}
      </TableCellTd>
      <TableCellTd header={headers[tabHeader].title}>
        {canInvite ? (
          <Button.Quaternary
            onClick={() => {
              handleInvite(praksissted.id);
            }}
            data-cy={'invite-btn'}
          >
            {inviteUserButtonLabel}
          </Button.Quaternary>
        ) : (
          ''
        )}
      </TableCellTd>
    </TableRow>
  );
};

// TODO Remove, when no longer needed
export const UnderUtvikling: React.FC<
  React.PropsWithChildren<unknown>
> = () => (
  <EmptyState
    title={'Siden er under utvikling'}
    text={
      'Siden finnes ikke enda, men vil dukke opp når funksjonaliteten er ferdigutviklet.'
    }
  />
);

export default PracticePlacesPage;
